import { useState } from 'react';

/**
 * Tile selection on a profile page that edits one tile at a time. Leaving a
 * tile in edit mode ends editing; with unsaved changes the page asks first
 * (Save / Discard / Stay).
 *
 * @param {Object} params
 * @param {boolean} params.isEditing
 * @param {string[]} params.dirtySections - tiles with unsaved changes
 * @param {(id: string) => void} params.setSelected
 * @param {() => void} params.stopEditing
 * @param {{current: {save: () => Promise<boolean>, discard: () => void}|null}} params.formActionsRef
 * @param {string} params.activeTile
 */
export default function useProfileTileSwitch({
  isEditing,
  dirtySections,
  setSelected,
  stopEditing,
  formActionsRef,
  activeTile,
}) {
  const [pendingTile, setPendingTile] = useState(null);
  const [saving, setSaving] = useState(false);

  const selectTile = (id) => {
    if (id === activeTile) return;
    if (!isEditing) {
      setSelected(id);
      return;
    }
    if (dirtySections.length) {
      setPendingTile(id);
      return;
    }
    stopEditing();
    setSelected(id);
  };

  const saveAndLeave = async () => {
    setSaving(true);
    const saved = await formActionsRef.current?.save();
    setSaving(false);
    // A failed save stays on the tile, with its errors shown.
    if (saved) setSelected(pendingTile);
    setPendingTile(null);
  };

  const discardAndLeave = () => {
    formActionsRef.current?.discard();
    stopEditing();
    setSelected(pendingTile);
    setPendingTile(null);
  };

  return {
    selectTile,
    leavePrompt: {
      open: !!pendingTile,
      saving,
      onSave: saveAndLeave,
      onDiscard: discardAndLeave,
      onStay: () => setPendingTile(null),
    },
  };
}
